// @givmo/auth — OAuth provider factories.
//
// Each factory returns a freshly-configured provider so a stale `setCustomParameters` can't leak
// between attempts. Ported from:
//   • Google/Microsoft: webapp `AuthDatasourceImpl.googleSignIn`/`microsoftSignIn`
//     (`GoogleAuthProvider`/`MicrosoftAuthProvider` with `prompt: 'select_account'`). The web SDK has
//     no `MicrosoftAuthProvider` class — Microsoft is `new OAuthProvider('microsoft.com')`, exactly
//     as the iOS app does (`OAuthProvider(providerID: .custom("microsoft.com"))`).
//   • Apple/Facebook: iOS `AuthService.signInWithApple`/`signInWithFacebook` parity reference.

import {
  GoogleAuthProvider,
  OAuthProvider,
  FacebookAuthProvider,
  type AuthProvider,
} from 'firebase/auth';

/** Google provider with the account chooser forced (`prompt=select_account`) — matches webapp + iOS. */
export function googleProvider(): GoogleAuthProvider {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  return provider;
}

/**
 * Microsoft provider. The web SDK has no `MicrosoftAuthProvider`; Microsoft is a generic OIDC OAuth
 * provider keyed on `microsoft.com` (the webapp Flutter `MicrosoftAuthProvider` compiles to the same
 * thing). Forces the account chooser via `prompt=select_account`, matching webapp.
 */
export function microsoftProvider(): OAuthProvider {
  const provider = new OAuthProvider('microsoft.com');
  provider.setCustomParameters({ prompt: 'select_account' });
  return provider;
}

/**
 * Apple provider (`apple.com`). Requests the `email` + `name` scopes — Apple only returns name/email
 * on the FIRST authorization (the iOS `requestedScopes = [.fullName, .email]` parity). On the web,
 * Firebase manages the nonce internally for `signInWithPopup`/`Redirect`, so unlike the native iOS
 * path we do not hand-roll the nonce.
 */
export function appleProvider(): OAuthProvider {
  const provider = new OAuthProvider('apple.com');
  provider.addScope('email');
  provider.addScope('name');
  return provider;
}

/**
 * Facebook provider with `public_profile` + `email` scopes (iOS parity:
 * `permissions: [.publicProfile, .email]`). On the web, popup/redirect drive the classic
 * access-token flow; the iOS Limited-Login (OIDC nonce) token shape is a native-only concern, so the
 * web provider is the classic `FacebookAuthProvider`. The post-sign-in Graph-email fallback the iOS
 * app does is a consumer-flow concern, handled in `consumer.ts`.
 */
export function facebookProvider(): FacebookAuthProvider {
  const provider = new FacebookAuthProvider();
  provider.addScope('public_profile');
  provider.addScope('email');
  return provider;
}

/** A provider keyed by the Givmo provider id, for generic strategy callers. */
export type GivmoOAuthProviderId = 'google' | 'microsoft' | 'apple' | 'facebook';

/** Build a provider by id. Centralises the factory map so `signInWithStrategy` callers stay terse. */
export function providerFor(id: GivmoOAuthProviderId): AuthProvider {
  switch (id) {
    case 'google':
      return googleProvider();
    case 'microsoft':
      return microsoftProvider();
    case 'apple':
      return appleProvider();
    case 'facebook':
      return facebookProvider();
  }
}
